import { useNavigate, useLocation } from 'react-router-dom';
import { Home, PlusCircle, Package, User } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

const tabs = [
  { path: '/', icon: Home, labelKey: 'header.nav.home' as const },
  { path: '/toy', icon: PlusCircle, labelKey: 'header.nav.create' as const },
  { path: '/track', icon: Package, labelKey: 'header.nav.orders' as const },
  { path: '/account', icon: User, labelKey: 'header.nav.account' as const },
];

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Hide on wizard sub-pages (they have their own navigation)
  const hideOnPrefixes = ['/toy', '/story'];
  const isWizardPage = hideOnPrefixes.some(
    (p) => location.pathname.startsWith(p) && location.pathname !== p
  );
  if (isWizardPage) return null;

  return (
    <nav
      aria-label={t('nav.bottom')}
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden safe-area-bottom"
    >
      <div className="flex items-stretch border-t border-border bg-background/80 backdrop-blur-lg h-14">
        {tabs.map((tab) => {
          const isActive = location.pathname === tab.path;
          const Icon = tab.icon;
          const label = t(tab.labelKey);

          return (
            <button
              key={tab.path}
              onClick={() => navigate(tab.path)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors relative',
                isActive
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              )}
              aria-current={isActive ? 'page' : undefined}
              aria-label={label}
            >
              {isActive && (
                <motion.span
                  layoutId="bottomNavIndicator"
                  className="absolute top-0 left-1/4 right-1/4 h-0.5 bg-primary rounded-full"
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              )}
              <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
              <span className="text-[10px] font-medium leading-none">
                {label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
